import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import {
  VerificationBadge,
  type VerificationStatus,
} from "~/components/common/VerificationBadge";
import { GlobeIcon } from "~/components/common/icons";

type Props = {
  id: string;
  name: string | null;
  commonName: string | null;
  description: string | null;
  logoUrl: string | null;
  verification: VerificationStatus;
  country?: string | null;
};

const verificationLabelKey: Record<VerificationStatus, string> = {
  verified: "Verification.verified",
  pending: "Verification.pending",
  unverified: "Verification.unverified",
};

export const MerchantRow = ({
  id,
  name,
  commonName,
  description,
  logoUrl,
  verification,
  country,
}: Props) => {
  const { t } = useTranslation();

  return (
    <Link
      to={`/merchants/${id}`}
      className="flex items-start gap-4 rounded-xl border border-border-shell/60 dark:border-transparent bg-surface-blank p-5 shadow-simple transition hover:shadow-content"
    >
      {logoUrl?.startsWith("http") ? (
        <img
          src={logoUrl}
          alt=""
          className="h-12 w-12 shrink-0 rounded-lg bg-surface-elevated object-cover p-1"
        />
      ) : (
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-raytio-viola/15 font-heading text-base font-bold text-raytio-viola-text">
          {Array.from(name ?? "?")[0]}
        </div>
      )}

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <h3 className="font-heading font-semibold text-text-full">
            {name ?? t("MerchantCard.unnamed")}
          </h3>
          <VerificationBadge
            status={verification}
            label={t(verificationLabelKey[verification])}
          />
        </div>
        {commonName && <p className="text-sm text-text-light">{commonName}</p>}
        {description && (
          <p className="mt-2 line-clamp-2 text-sm text-text-light">
            {description}
          </p>
        )}
      </div>

      {country && (
        <span className="hidden shrink-0 items-center gap-1.5 text-xs text-text-medium sm:inline-flex">
          <GlobeIcon className="h-3.5 w-3.5" />
          {country}
        </span>
      )}
    </Link>
  );
};
